import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
} from '../../../lib/editing-configs';
import UniversalText from '../../universal-text/universal-text';
import UniversalButton from '../../universal-button/universal-button';
import YouTubePlayer from '../../../lib/youtube-player/youtube-player';
import Mp4VideoPlayer from '../../../lib/mp4-video-player/mp4-video-player';
import InstagramPlayer from '../../../lib/instagram-player/instagram-player';
import RetirementCalculatorSC from '../../retirement-calculator-sc/retirement-calculator-sc';
import SpotifyPlayer from '../../../lib/spotify-player';
import SoundCloudPlayer from '../../../lib/soundcloud-player';
import TikTokPlayer from '../../../lib/tiktok-player';
import UniversalAccordion from '../../universal-accordion/universal-accordion';
import UniversalImage from '../../universal-image/universal-image';
import BainReferralBannerSC from '../../bain-referral-banner-sc/bain-referral-banner-sc';
import BorderBanner from '../../border-banner/border-banner';
import AdvertiserDisclosureWrapper from '../../advertiser-disclosure/advertiser-disclosure';
import LottieAnimation from '../../lottie-animation/lottie-animation';
import UniversalContainer from '../../universal-container';
import CurrentDate from '../../current-date/current-date';
import UniversalBulletList from '../../universal-bullet-list/universal-bullet-list';
import ThirdPartyEmbed from '../../third-party-embed/third-party-embed';
import InputButton from '../../input-button/input-button';
import { componentTypes } from './component-types';

export default (componentDoc) => {
  // make sure all gridItems has '$unique_key'
  const filteredGridItems = componentDoc?.props?.gridItems?.filter(
    (gridItem) => '$unique_key' in gridItem
  );

  const componentDocument = {
    ...componentDoc,
    // update componentDocument with sanitized gridItems
    props: { ...componentDoc?.props, gridItems: filteredGridItems },
  };

  const centeredInParentBackgroundInputConfig = componentDocument?.props
    ?.centeredInParent
    ? [
        ...UniversalContainer.getEditingConfig(
          componentDocument,
          'Centered Parent Background',
          ['backgroundColor', 'backgroundImage'],
          'centeredInParentBackground',
          {
            disableGroup: true,
            customLabels: {
              background: 'Centered In Parent Background',
            },
          }
        ),
      ]
    : [];

  const floatingGridControls = componentDocument?.props?.floatingGrid
    ? [
        group('Floating Grid Options', [
          stringInputSpec('floatTop', 'Float Top', {
            help: 'Defaults to "0"',
          }),
          stringInputSpec('floatBottom', 'Float Bottom', {
            help: 'Defaults to "0"',
          }),
          stringInputSpec('floatLeft', ' Float Left', {
            help: 'Defaults to "0"',
          }),
          stringInputSpec('floatRight', ' Float Right', {
            help: 'Defaults to "0"',
          }),
          numberInputSpec('floatIndex', 'Float Index'),
        ]),
      ]
    : [];

  return [
    boolCheckboxSpec(
      'disableDefaultProps',
      'Disable Grid Outline/Transparency?'
    ),
    stringSelectSpec(
      'columns',
      'Number of columns',
      [
        {
          value: '',
          label: 'Auto',
        },
        {
          value: '1',
          label: '1',
        },
        {
          value: '2',
          label: '2',
        },
        {
          value: '3',
          label: '3',
        },
        {
          value: '4',
          label: '4',
        },
        {
          value: '5',
          label: '5',
        },
      ],
      {
        help: 'Number of columns in layout on desktop',
      }
    ),

    boolCheckboxSpec('floatingGrid', 'Floating Grid?'),
    ...floatingGridControls,

    group('Grid Container', [
      numberInputSpec('gap', 'Gap', {
        help: 'Space between grid items in pixels. Defaults to "0" ',
      }),
      boolCheckboxSpec('centeredInParent', 'Centered In Parent'),
      boolCheckboxSpec('fitWidth', 'Fit Width?'),
      boolCheckboxSpec('doNotWrapRow', 'Do not wrap row?'),
      stringSelectSpec(
        'justifyContent',
        'Justify Content',
        [
          { label: 'Space Between', value: 'space-between' },
          { label: 'Space Around', value: 'space-around' },
          { label: 'Start', value: 'flex-start' },
          { label: 'Center', value: 'center' },
          { label: 'End', value: 'flex-end' },
        ],
        {
          help: 'Defines how grid items are aligned in the container. Defaults to "space-between"',
        }
      ),
      ...centeredInParentBackgroundInputConfig,
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Grid Container',
        [
          'backgroundColor',
          'backgroundImage',
          'border',
          'margin',
          'padding',
          'hide',
          'jumpLinkId',
          'stickyFooter',
        ],
        'container',
        {
          disableGroup: true,
        }
      ),
    ]),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'width',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'title'
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'width',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'subtitle',
      { text: 'text' }
    ),
    repeatable('gridItems', '', 'Grid Item', (item) => [
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Grid Item Container',
        [
          'backgroundColor',
          'backgroundImage',
          'border',
          'margin',
          'padding',
          'width',
          'alignment',
          'hide',
        ],
        `gridItems.${item}.itemContainer`
      ),
      repeatable('itemComponents', '', 'Component', (component) => {
        const componentType =
          componentDocument?.props?.gridItems?.[item]?.itemComponents?.[
            component
          ]?.type;

        const textInputConfig =
          componentType === componentTypes.TEXT
            ? [
                ...UniversalText.getEditingConfig(
                  componentDocument,
                  'Text Options',
                  [
                    'fontWeight',
                    'groupButton',
                    'width',
                    'margin',
                    'padding',
                    'fontFamily',
                    'textColor',
                    'textSize',
                    'textType',
                    'placement',
                  ],
                  `gridItems.${item}.itemComponents.${component}.text`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const imageInputConfig =
          componentType === componentTypes.IMAGE
            ? [
                ...UniversalImage.getEditingConfig(
                  componentDocument,
                  'Image Options',
                  ['border', 'margin', 'padding'],
                  `gridItems.${item}.itemComponents.${component}.image`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const youtubeInputConfig =
          componentType === componentTypes.YOUTUBE
            ? [
                ...YouTubePlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.youtube`,
                  true
                ),
              ]
            : [];

        const mp4VideoInputConfig =
          componentType === componentTypes.MP4VIDEO
            ? [
                ...Mp4VideoPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.mp4Video`,
                  true
                ),
              ]
            : [];

        const spotifyInputConfig =
          componentType === componentTypes.SPOTIFY
            ? [
                ...SpotifyPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.spotify`,
                  true
                ),
              ]
            : [];

        const soundCloudInputConfig =
          componentType === componentTypes.SOUNDCLOUD
            ? [
                ...SoundCloudPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.soundcloud`,
                  true
                ),
              ]
            : [];

        const tiktokInputConfig =
          componentType === componentTypes.TIKTOK
            ? [
                ...TikTokPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.tiktok`
                ),
              ]
            : [];

        const instagramInputConfig =
          componentType === componentTypes.INSTAGRAM
            ? [
                ...InstagramPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.instagram`
                ),
              ]
            : [];

        const buttonInputConfig =
          componentType === componentTypes.BUTTON
            ? [
                ...UniversalButton.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.button`,
                  'Button Options',
                  [],
                  { disableGroup: true, includeContainer: true }
                ),
              ]
            : [];

        const bulletListInputConfig =
          componentType === componentTypes.BULLETLIST
            ? [
                ...UniversalBulletList.getEditingConfig(
                  componentDocument,
                  'Bullet list options',
                  `gridItems.${item}.itemComponents.${component}.bulletList`,
                  { disableGroup: true, includeContainer: true }
                ),
              ]
            : [];

        const bainReferralBannerConfig =
          componentType === componentTypes.BAINREFERRALBANNER
            ? [
                ...BainReferralBannerSC.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.bainReferralBanner`
                ),
              ]
            : [];

        const borderBannerConfig =
          componentType === componentTypes.BORDERBANNER
            ? [
                ...BorderBanner.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.borderBanner`
                ),
              ]
            : [];
        const advertiserDisclosureConfig =
          componentType === componentTypes.ADVERTISERDISCLOSURE
            ? [
                ...AdvertiserDisclosureWrapper.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.advertiserDisclosure`
                ),
              ]
            : [];
        const accordionConfig =
          componentType === componentTypes.ACCORDION
            ? [
                ...UniversalAccordion.getEditingConfig(
                  componentDocument,
                  'Accordion',
                  `gridItems.${item}.itemComponents.${component}.accordion`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const lottieConfig =
          componentType === componentTypes.LOTTIE
            ? [
                ...LottieAnimation.getEditingConfig(
                  componentDocument,
                  'Lottie Animation',
                  `gridItems.${item}.itemComponents.${component}.lottie`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const currentDateConfig =
          componentType === componentTypes.CURRENTDATE
            ? [
                ...CurrentDate.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.currentDate`
                ),
              ]
            : [];

        const thirdPartyEmbedConfig =
          componentType === componentTypes.THIRDPARTYEMBED
            ? [
                ...ThirdPartyEmbed.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.thirdPartyEmbed`
                ),
              ]
            : [];

        const inputButtonConfig =
          componentType === componentTypes.INPUTBUTTON
            ? [
                ...InputButton.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.inputButton`
                ),
              ]
            : [];

        const retirementCalculatorConfig =
          componentType === componentTypes.RETIREMENTCALCULATORSC
            ? [
                ...RetirementCalculatorSC.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.retirementCalculator`
                ),
              ]
            : [];

        return [
          !componentType
            ? stringSelectSpec('type', 'Component List', [
                { label: 'Select component', value: 'none' },
                {
                  label: 'Advertiser Disclosure',
                  value: componentTypes.ADVERTISERDISCLOSURE,
                },
                { label: 'Accordion', value: componentTypes.ACCORDION },
                {
                  label: 'Bain Referral Banner SC',
                  value: componentTypes.BAINREFERRALBANNER,
                },
                {
                  label: 'Border Banner',
                  value: componentTypes.BORDERBANNER,
                },
                { label: 'Button', value: componentTypes.BUTTON },
                { label: 'Bullet list', value: componentTypes.BULLETLIST },
                { label: 'Current Date', value: componentTypes.CURRENTDATE },
                { label: 'Input Button', value: componentTypes.INPUTBUTTON },
                { label: 'Image', value: componentTypes.IMAGE },
                { label: 'Instagram', value: componentTypes.INSTAGRAM },
                { label: 'Lottie Animation', value: componentTypes.LOTTIE },
                { label: 'MP4 Video', value: componentTypes.MP4VIDEO },
                {
                  label: 'Retirement Calculator SC',
                  value: componentTypes.RETIREMENTCALCULATORSC,
                },
                { label: 'SoundCloud', value: componentTypes.SOUNDCLOUD },
                { label: 'Spotify', value: componentTypes.SPOTIFY },
                { label: 'Text', value: componentTypes.TEXT },
                { label: 'TikTok', value: componentTypes.TIKTOK },
                { label: 'YouTube', value: componentTypes.YOUTUBE },
                {
                  label: 'Third Party Embed',
                  value: componentTypes.THIRDPARTYEMBED,
                },
              ])
            : [],
          ...advertiserDisclosureConfig,
          ...accordionConfig,
          ...bainReferralBannerConfig,
          ...borderBannerConfig,
          ...buttonInputConfig,
          ...bulletListInputConfig,
          ...imageInputConfig,
          ...instagramInputConfig,
          ...lottieConfig,
          ...mp4VideoInputConfig,
          ...soundCloudInputConfig,
          ...spotifyInputConfig,
          ...textInputConfig,
          ...tiktokInputConfig,
          ...youtubeInputConfig,
          ...currentDateConfig,
          ...thirdPartyEmbedConfig,
          ...inputButtonConfig,
          ...retirementCalculatorConfig,
        ];
      }),
    ]),
  ];
};
